import React, { useMemo, useState } from 'react'

import useAxios, { mutate } from 'hooks/useAxios'
import useSaveUserActivityLog from 'hooks/useSaveUserActivityLog'
import useUser from 'hooks/useUser'
import { toast } from 'libs/toast'
import { validateRequiredText } from 'libs/form-validation'
import { buildTeamMemberDropdownOption } from 'libs/team-member-dropdown-option'
import { logSaveTeamMemberGroup } from 'components/TeamMembers/utils'

import TeamMemberGroupModal from '.'

interface TeamMember {
  id: number
  name: string
  email: string
  member_id: string
}

interface TeamMemberGroupModalContainerProps {
  mode: 'create' | 'edit'
  groupToken?: string
  initialGroupName?: string
  initialMemberIds?: number[]
  onClose: () => void
  onSuccess?: (newGroupToken?: string) => void
}

export default function TeamMemberGroupModalContainer({
  mode,
  groupToken,
  initialGroupName = '',
  initialMemberIds = [],
  onClose,
  onSuccess,
}: TeamMemberGroupModalContainerProps) {
  const [groupName, setGroupName] = useState(initialGroupName)
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>(initialMemberIds)
  const [nameError, setNameError] = useState('')
  const userInfo = useUser()
  const companyId = userInfo?.selected_workspace?.id
  const saveUserActivityLog = useSaveUserActivityLog()

  const { data: teamMembersData, loading: isLoadingMembers } = useAxios({
    url: 'team-members/team-member-list/?limit=1000',
    method: 'POST',
  })

  const { fetch: createGroup, loading: isCreating } = useAxios({
    url: 'team-members/groups/',
    method: 'POST',
    skip: true,
  })

  const { fetch: updateGroup, loading: isUpdating } = useAxios({
    url: `team-members/groups/${groupToken}/`,
    method: 'PATCH',
    skip: true,
  })

  const teamMembers: TeamMember[] = teamMembersData?.results ?? []
  const memberOptions = useMemo(() => teamMembers.map(buildTeamMemberDropdownOption), [teamMembers])

  function handleGroupNameChange(name: string) {
    setGroupName(name)
    if (nameError) {
      setNameError('')
    }
  }

  function handleSave() {
    const validationError = validateRequiredText('Group name', groupName)
    if (validationError) {
      setNameError(validationError)
      return
    }

    const data = { name: groupName, member_ids: selectedMemberIds }
    const fetcher = mode === 'create' ? createGroup : updateGroup
    fetcher({ data })
      .then((response) => {
        if (response?.status === 201 || response?.status === 200) {
          mutate('team-members/groups/')
          if (mode === 'edit' && groupToken) {
            mutate(`team-members/groups/${groupToken}/`)
          }
          onSuccess?.(response.data?.token)
          toast.success(
            mode === 'create' ? 'Group created successfully.' : 'Group updated successfully.'
          )
          logSaveTeamMemberGroup(
            saveUserActivityLog,
            companyId,
            mode === 'create',
            data,
            response.status
          )
          onClose()
        }
      })
      .catch((error: any) => {
        toast.error(
          mode === 'create'
            ? 'Could not create group. Contact support for help.'
            : 'Could not update group. Contact support for help.'
        )
        logSaveTeamMemberGroup(
          saveUserActivityLog,
          companyId,
          mode === 'create',
          data,
          error?.response?.status ?? 500
        )
      })
  }

  return (
    <TeamMemberGroupModal
      title={mode === 'create' ? 'Create group' : 'Edit group'}
      saveLabel={mode === 'create' ? 'Create group' : 'Save changes'}
      groupName={groupName}
      selectedMemberIds={selectedMemberIds}
      memberOptions={memberOptions}
      isSaving={isCreating || isUpdating}
      isLoadingMembers={isLoadingMembers}
      nameError={nameError}
      onGroupNameChange={handleGroupNameChange}
      onMembersChange={setSelectedMemberIds}
      onSave={handleSave}
      onClose={onClose}
    />
  )
}



// WEBPACK FOOTER //
// ./src/components/TeamMemberGroupModal/container.tsx